import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
  Tabs,
} from '@mui/material';
import { ArrowLeft, Pause, Pencil, Play, Target } from 'lucide-react';
import { CampaignFormDialog } from '../components/CampaignFormDialog';
import { CampaignStatusChip } from '../components/CampaignStatusChip';
import { CouponsTab } from '../components/CouponsTab';
import { IssuedTab } from '../components/IssuedTab';
import { RedemptionsTab } from '../components/RedemptionsTab';
import { TargetsDialog } from '../components/TargetsDialog';
import {
  useCampaign,
  usePatchCampaign,
  useReplaceTargets,
  useSetCampaignStatus,
} from '../hooks/useCampaigns';
import { discountLabel, errorText, formatDateTime, formatVnd } from '../format';
import { useNotification } from '../../../../hooks/useNotification';

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-lg border border-gray-200 p-3 dark:border-slate-700">
    <div className="text-xs text-gray-500 dark:text-slate-400">{label}</div>
    <div className="text-lg font-semibold">{value}</div>
  </div>
);

export const CampaignDetailPage: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const notification = useNotification();
  const { data: campaign, isLoading, error } = useCampaign(publicId);
  const patch = usePatchCampaign();
  const setStatus = useSetCampaignStatus();
  const replaceTargets = useReplaceTargets();
  const [tab, setTab] = useState<'coupons' | 'issued' | 'redemptions'>('coupons');
  const [editOpen, setEditOpen] = useState(false);
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [confirm, setConfirm] = useState<'activate' | 'pause' | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);

  if (isLoading)
    return (
      <div className="flex justify-center p-10">
        <CircularProgress />
      </div>
    );
  if (error || !campaign || !publicId) {
    return (
      <div className="p-6">
        <Alert severity="error">{error ? errorText(error) : 'Campaign not found'}</Alert>
      </div>
    );
  }

  const stats = campaign.stats;
  const rulesLocked = (stats?.reserved ?? 0) + (stats?.redeemed ?? 0) > 0 || campaign.usedCount > 0;
  const ended = new Date(campaign.validTo) < new Date();
  const targetNames = campaign.targets
    .map((t) => t.package?.name ?? t.tokenPack?.name)
    .filter(Boolean);

  const runStatus = () => {
    if (!confirm) return;
    setStatus.mutate(
      { publicId, action: confirm },
      {
        onSuccess: () => {
          notification.success(confirm === 'activate' ? 'Campaign activated' : 'Campaign paused');
          setConfirm(null);
        },
        onError: (err) => {
          notification.error(errorText(err));
          setConfirm(null);
        },
      }
    );
  };

  return (
    <div className="space-y-5 p-6">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-slate-400"
        onClick={() => navigate('/campaigns')}
      >
        <ArrowLeft size={14} /> Campaigns
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold">{campaign.name}</h1>
            <CampaignStatusChip status={campaign.status} ended={ended} />
          </div>
          {campaign.note && (
            <p className="text-sm text-gray-600 dark:text-slate-400">{campaign.note}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            startIcon={<Pencil size={16} />}
            onClick={() => {
              setDialogError(null);
              setEditOpen(true);
            }}
          >
            Edit
          </Button>
          <Button
            startIcon={<Target size={16} />}
            onClick={() => {
              setDialogError(null);
              setTargetsOpen(true);
            }}
          >
            Targets
          </Button>
          {campaign.status === 'ACTIVE' ? (
            <Button
              color="warning"
              variant="outlined"
              startIcon={<Pause size={16} />}
              onClick={() => setConfirm('pause')}
            >
              Pause
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<Play size={16} />}
              onClick={() => setConfirm('activate')}
            >
              Activate
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Discount" value={discountLabel(campaign)} />
        <Stat
          label="Reserved / redeemed"
          value={`${stats?.reserved ?? 0} / ${stats?.redeemed ?? 0}`}
        />
        <Stat
          label="Used / limit"
          value={`${campaign.usedCount} / ${campaign.maxRedemptions ?? '∞'}`}
        />
        <Stat label="Total discount given" value={formatVnd(stats?.totalDiscountMinor ?? 0)} />
      </div>

      <div className="space-y-1 text-sm text-gray-700 dark:text-slate-300">
        <div>
          Valid: {formatDateTime(campaign.validFrom)} → {formatDateTime(campaign.validTo)}
        </div>
        <div>
          Applies to: {campaign.appliesTo}
          {targetNames.length > 0 ? ` — ${targetNames.join(', ')}` : ' (all eligible items)'}
        </div>
        <div>
          Per user: {campaign.perUserLimit}
          {campaign.minOrderMinor ? ` · Min order ${formatVnd(campaign.minOrderMinor)}` : ''}
          {campaign.newPayersOnly ? ' · First-time buyers only' : ''}
        </div>
        {campaign.status === 'DRAFT' && (
          <Alert severity="info" className="mt-2">
            This campaign is a draft: codes do not work until you activate it.
          </Alert>
        )}
      </div>

      <Tabs value={tab} onChange={(_, value) => setTab(value)}>
        <Tab
          value="coupons"
          label={`Coupons${campaign._count ? ` (${campaign._count.coupons})` : ''}`}
        />
        {campaign.issueTrigger !== 'NONE' && <Tab value="issued" label="Issued automatically" />}
        <Tab value="redemptions" label="Redemptions" />
      </Tabs>
      {tab === 'coupons' && <CouponsTab campaignId={publicId} campaignName={campaign.name} />}
      {tab === 'issued' && <IssuedTab campaignId={publicId} />}
      {tab === 'redemptions' && <RedemptionsTab campaignId={publicId} />}

      <CampaignFormDialog
        open={editOpen}
        campaign={campaign}
        rulesLocked={rulesLocked}
        saving={patch.isPending}
        error={dialogError}
        onClose={() => setEditOpen(false)}
        onSubmit={(input) => {
          setDialogError(null);
          const {
            name,
            note,
            newPayersOnly,
            perUserLimit,
            maxRedemptions,
            validFrom,
            validTo,
            minOrderMinor,
            maxDiscountMinor,
          } = input;
          const base = {
            name,
            note,
            newPayersOnly,
            perUserLimit,
            maxRedemptions,
            validFrom,
            validTo,
            minOrderMinor,
            maxDiscountMinor,
          };
          patch.mutate(
            { publicId, input: rulesLocked ? base : input },
            {
              onSuccess: () => {
                notification.success('Campaign updated');
                setEditOpen(false);
              },
              onError: (err) => setDialogError(errorText(err)),
            }
          );
        }}
      />
      <TargetsDialog
        open={targetsOpen}
        appliesTo={campaign.appliesTo}
        targets={campaign.targets}
        saving={replaceTargets.isPending}
        error={dialogError}
        onClose={() => setTargetsOpen(false)}
        onSave={(targets) => {
          setDialogError(null);
          replaceTargets.mutate(
            { publicId, targets },
            {
              onSuccess: () => {
                notification.success('Targets saved');
                setTargetsOpen(false);
              },
              onError: (err) => setDialogError(errorText(err)),
            }
          );
        }}
      />
      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        <DialogTitle>
          {confirm === 'activate' ? 'Activate campaign?' : 'Pause campaign?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm === 'activate'
              ? 'Customers will be able to use this campaign’s codes right away.'
              : 'New orders can no longer use these codes. Orders that already reserved a code keep their price.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={runStatus} disabled={setStatus.isPending}>
            {confirm === 'activate' ? 'Activate' : 'Pause'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
